import React, { useRef, useState } from 'react'
import { Icon } from '@chakra-ui/icons'
import { Button } from '@chakra-ui/react'
import { Box, Flex } from '@chakra-ui/react'
import { FaPaperPlane } from 'react-icons/fa6'

import TeamChatInputTextarea from './input'
import { MentionTextareaRef } from '~components/shared/inputs/mention-textarea'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import {
    namedOperations,
    useCreateInternalChatMessageMutation,
} from '~graphql/generated/graphql'
import { useTeamChatConversation } from '../provider'
import { useTeamChatAttachments } from './attachments/provider'
import TeamChatInputAttachmentsPreview from './attachments/preview'
import TeamChatInputAttachmentsButton from './attachments/button'

export default function TeamChatInput() {
    const { internalChat, currentInternalChatUser } = useTeamChatConversation()
    const textareaRef = useRef<MentionTextareaRef>(null)
    const [text, setText] = useState('')
    const { attachments, setAttachments } = useTeamChatAttachments()

    const { onCompleted, onError } = useToastFeedback(
        'Message sent',
        'Failed to send message',
    )

    const [createMessage, { loading }] = useCreateInternalChatMessageMutation({
        onCompleted: data => {
            if (data.createInternalChatMessage) {
                onCompleted()
            }
        },
        onError: error => {
            console.error(error)
            onError()
        },
    })

    async function handleSendMessage() {
        if ((!text && attachments.length === 0) || !currentInternalChatUser) {
            onError()
            console.error('Missing text or user', {
                text,
                currentInternalChatUser,
            })
            return
        }

        try {
            await createMessage({
                variables: {
                    data: {
                        text,
                        sender: currentInternalChatUser.id,
                        internal_chat: internalChat.id,
                        internal_chat_messages_attachments: attachments,
                    },
                },
            })
            setText('')
            setAttachments([])
        } catch (error) {
            console.error(error)
            onError()
        }
    }

    return (
        <Box px={4} pb={4} h="fit-content">
            <Flex
                flexDir="column"
                borderWidth={1}
                borderColor="gray.200"
                rounded="xl"
                bg="gray.50"
                boxSizing="border-box"
                flex={1}
                shadow="blue.md"
            >
                <TeamChatInputTextarea
                    ref={textareaRef}
                    value={text}
                    onChange={setText}
                />
                <TeamChatInputAttachmentsPreview />
                <Flex
                    justify="space-between"
                    w="full"
                    align="center"
                    px={2}
                    pb={2}
                >
                    <TeamChatInputAttachmentsButton />
                    <Button
                        size="sm"
                        isLoading={loading}
                        loadingText="Sending..."
                        isDisabled={!text && attachments.length === 0}
                        variant={
                            !text && attachments.length === 0
                                ? 'ghost'
                                : 'solid'
                        }
                        colorScheme={
                            !text && attachments.length === 0 ? 'gray' : 'brand'
                        }
                        leftIcon={<Icon as={FaPaperPlane} />}
                        onClick={() => {
                            handleSendMessage()
                        }}
                    >
                        Send
                    </Button>
                </Flex>
            </Flex>
        </Box>
    )
}
